import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, Plus, Trash2, Eye, DollarSign, Calendar, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Users, AlertCircle, CheckCircle, X, Printer, Download, Clock, User, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { escapeHtml, openPrintWindow } from "../utils/sanitize";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PayrollPage = () => {
  const navigate = useNavigate();
  const [payroll, setPayroll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [expanded, setExpanded] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAttModal, setShowAttModal] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [addForm, setAddForm] = useState({ name: "", position: "", salary: "" });
  const [attForm, setAttForm] = useState({ date: new Date().toISOString().split("T")[0], status: "absent", reason: "", deduction_amount: 20000 });
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchPayroll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/payroll?month=${month}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setPayroll(await res.json());
    } catch (err) { console.error("Operation error:", err); }
    setLoading(false);
  }, [month, token]);

  useEffect(() => { fetchPayroll(); }, [fetchPayroll]);

  const changeMonth = (dir) => {
    const d = new Date(month + "-01");
    d.setMonth(d.getMonth() + dir);
    setMonth(d.toISOString().slice(0, 7));
  };

  const monthLabel = () => {
    const d = new Date(month + "-01");
    return d.toLocaleDateString("en-GB", { year: "numeric", month: "long" });
  };

  const isCurrentMonth = month === new Date().toISOString().slice(0, 7);

  // Pay salary
  const handlePay = async () => {
    if (!payAmount || !selectedEmp) return;
    try {
      const res = await fetch(`${API}/employees/${selectedEmp.id}/pay`, {
        method: "POST", headers, body: JSON.stringify({ amount: parseFloat(payAmount), date: payDate })
      });
      if (res.ok) {
        toast.success(`تم دفع ${parseInt(payAmount).toLocaleString()} لـ ${selectedEmp.name}`);
        setShowPayModal(false);
        setPayAmount("");
        fetchPayroll();
      } else { const d = await res.json(); toast.error(d.detail || "خطأ"); }
    } catch (err) { console.error(err); toast.error("خطأ في الدفع"); }
  };

  // Add employee
  const handleAdd = async () => {
    if (!addForm.name.trim()) { toast.error("أدخل اسم الموظف"); return; }
    try {
      const res = await fetch(`${API}/employees`, {
        method: "POST", headers, body: JSON.stringify({ name: addForm.name, position: addForm.position, salary: parseFloat(addForm.salary) || 0 })
      });
      if (res.ok) {
        toast.success("تمت إضافة الموظف");
        setShowAddModal(false);
        setAddForm({ name: "", position: "", salary: "" });
        fetchPayroll();
      } else { const d = await res.json(); toast.error(d.detail || "خطأ"); }
    } catch (err) { console.error(err); toast.error("خطأ"); }
  };

  // Delete employee
  const handleDelete = async (id, name) => {
    if (!window.confirm(`حذف الموظف ${name}؟`)) return;
    try {
      await fetch(`${API}/employees/${id}`, { method: "DELETE", headers });
      toast.success("تم الحذف");
      fetchPayroll();
    } catch (err) { console.error(err); toast.error("خطأ"); }
  };

  // Add attendance
  const handleAttendance = async () => {
    if (!selectedEmp) return;
    try {
      const res = await fetch(`${API}/attendance`, {
        method: "POST", headers,
        body: JSON.stringify({ employee_id: selectedEmp.name, ...attForm })
      });
      if (res.ok) {
        toast.success("تم تسجيل الغياب");
        setShowAttModal(false);
        fetchPayroll();
      } else { const d = await res.json(); toast.error(d.detail || "خطأ"); }
    } catch (err) { console.error(err); toast.error("خطأ"); }
  };

  // Delete attendance
  const deleteAttendance = async (recordId) => {
    try {
      await fetch(`${API}/attendance/${recordId}`, { method: "DELETE", headers });
      toast.success("تم حذف السجل");
      fetchPayroll();
    } catch (err) { console.error("Operation error:", err); }
  };

  // Export to Excel
  const exportExcel = () => {
    if (!payroll?.employees?.length) return;
    const wb = XLSX.utils.book_new();
    const data = payroll.employees.map(e => ({
      "الاسم": e.name,
      "المنصب": e.position || "-",
      "الراتب": e.salary,
      "المدفوع": e.month_paid,
      "الخصومات": e.deductions,
      "المتبقي": e.net_remaining,
      "أيام غياب": e.absent_days,
      "نصف يوم": e.half_days,
      "إجازات": e.leave_days
    }));
    data.push({
      "الاسم": "المجموع",
      "المنصب": "",
      "الراتب": payroll.summary.total_salaries,
      "المدفوع": payroll.summary.total_paid,
      "الخصومات": payroll.summary.total_deductions,
      "المتبقي": payroll.summary.total_remaining,
      "أيام غياب": "", "نصف يوم": "", "إجازات": ""
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), "الرواتب");
    XLSX.writeFile(wb, `رواتب_${month}.xlsx`);
    toast.success("تم التصدير");
  };

  // Print
  const printPayroll = () => {
    if (!payroll?.employees?.length) return;
    const rows = payroll.employees.map(e =>
      `<tr><td>${escapeHtml(e.name)}</td><td>${escapeHtml(e.position||"-")}</td><td>${e.salary.toLocaleString()}</td><td>${e.month_paid.toLocaleString()}</td><td>${e.deductions.toLocaleString()}</td><td style="font-weight:bold">${e.net_remaining.toLocaleString()}</td></tr>`
    ).join("");
    const html = `<html dir="rtl"><head><style>body{font-family:Tajawal,sans-serif;padding:20px}table{width:100%;border-collapse:collapse;margin-top:20px}th,td{border:1px solid #ddd;padding:8px;text-align:right}th{background:#f5f5f5}.header{text-align:center;margin-bottom:10px}</style></head><body>
      <div class="header"><h2>كشف رواتب - ${monthLabel()}</h2><p>المركز الوطني التركي لزراعة الشعر</p></div>
      <table><thead><tr><th>الاسم</th><th>المنصب</th><th>الراتب</th><th>المدفوع</th><th>الخصومات</th><th>المتبقي</th></tr></thead><tbody>${rows}
      <tr style="font-weight:bold;background:#f0f9ff"><td>المجموع</td><td></td><td>${payroll.summary.total_salaries.toLocaleString()}</td><td>${payroll.summary.total_paid.toLocaleString()}</td><td>${payroll.summary.total_deductions.toLocaleString()}</td><td>${payroll.summary.total_remaining.toLocaleString()}</td></tr>
      </tbody></table></body></html>`;
    openPrintWindow(html);
  };

  const s = payroll?.summary;
  const emps = payroll?.employees || [];

  return (
    <div className="space-y-6" data-testid="payroll-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Wallet className="w-7 h-7 text-emerald-600" />
            إدارة الرواتب
          </h1>
          <p className="text-gray-500 text-sm mt-1">كشف رواتب شهري مفصّل للموظفين</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={exportExcel} className="flex items-center gap-1.5 px-3 py-2 bg-green-50 text-green-700 rounded-lg text-sm hover:bg-green-100" data-testid="export-payroll-btn">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button onClick={printPayroll} className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100" data-testid="print-payroll-btn">
            <Printer className="w-4 h-4" /> طباعة
          </button>
          <button onClick={() => setShowAddModal(true)} className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 font-medium" data-testid="add-employee-btn">
            <Plus className="w-4 h-4" /> إضافة موظف
          </button>
        </div>
      </div>

      {/* Month Selector */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-center gap-4">
          <button onClick={() => changeMonth(-1)} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="prev-month-btn">
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
          <div className="text-center min-w-[200px]">
            <h2 className="text-xl font-bold text-gray-900" data-testid="current-month">{monthLabel()}</h2>
            {isCurrentMonth && <span className="text-xs text-emerald-600 font-medium">الشهر الحالي</span>}
          </div>
          <button onClick={() => changeMonth(1)} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="next-month-btn">
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          {!isCurrentMonth && (
            <button onClick={() => setMonth(new Date().toISOString().slice(0, 7))} className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs hover:bg-emerald-100">
              الشهر الحالي
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="payroll-summary">
          <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
            <Users className="w-5 h-5 text-blue-500 mx-auto mb-1" />
            <div className="text-2xl font-bold text-gray-900">{s.total_employees}</div>
            <div className="text-xs text-gray-500">موظف</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
            <Wallet className="w-5 h-5 text-gray-500 mx-auto mb-1" />
            <div className="text-2xl font-bold text-gray-900">{s.total_salaries.toLocaleString()}</div>
            <div className="text-xs text-gray-500">إجمالي الرواتب</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
            <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
            <div className="text-2xl font-bold text-emerald-600">{s.total_paid.toLocaleString()}</div>
            <div className="text-xs text-gray-500">المدفوع</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
            <AlertCircle className="w-5 h-5 text-red-500 mx-auto mb-1" />
            <div className="text-2xl font-bold text-red-600">{s.total_deductions.toLocaleString()}</div>
            <div className="text-xs text-gray-500">الخصومات</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-blue-200 text-center bg-blue-50">
            <DollarSign className="w-5 h-5 text-blue-600 mx-auto mb-1" />
            <div className="text-2xl font-bold text-blue-700">{s.total_remaining.toLocaleString()}</div>
            <div className="text-xs text-blue-600 font-medium">المتبقي</div>
          </div>
        </div>
      )}

      {/* Employees List */}
      {loading ? (
        <div className="text-center py-16">
          <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-3"></div>
          <p className="text-gray-400">جاري التحميل...</p>
        </div>
      ) : emps.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">لا يوجد موظفين</p>
          <button onClick={() => setShowAddModal(true)} className="mt-3 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm">إضافة موظف</button>
        </div>
      ) : (
        <div className="space-y-3">
          {emps.map((emp) => {
            const pct = emp.salary > 0 ? Math.min(100, Math.round((emp.month_paid / emp.salary) * 100)) : 0;
            const isExpanded = expanded === emp.id;
            return (
              <div key={emp.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`emp-card-${emp.id}`}>
                {/* Employee Row */}
                <div className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-bold text-lg">
                        {emp.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 cursor-pointer hover:text-purple-600 hover:underline" onClick={() => navigate(`/employees/${emp.id}`)}>{emp.name}</p>
                        <p className="text-xs text-gray-500">{emp.position || "موظف"}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-center">
                      <div>
                        <p className="text-lg font-bold text-gray-800">{emp.salary.toLocaleString()}</p>
                        <p className="text-[10px] text-gray-400">الراتب</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-emerald-600">{emp.month_paid.toLocaleString()}</p>
                        <p className="text-[10px] text-gray-400">المدفوع</p>
                      </div>
                      {emp.deductions > 0 && (
                        <div>
                          <p className="text-lg font-bold text-red-500">-{emp.deductions.toLocaleString()}</p>
                          <p className="text-[10px] text-gray-400">خصم</p>
                        </div>
                      )}
                      <div className={`px-3 py-1.5 rounded-lg ${emp.net_remaining <= 0 ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                        <p className={`text-lg font-bold ${emp.net_remaining <= 0 ? 'text-green-600' : 'text-amber-700'}`}>
                          {emp.net_remaining.toLocaleString()}
                        </p>
                        <p className="text-[10px] text-gray-500">{emp.net_remaining <= 0 ? "مكتمل" : "متبقي"}</p>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-3">
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-500 ${pct >= 100 ? 'bg-green-500' : pct >= 50 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-[10px] text-gray-400">{pct}% مدفوع</span>
                      {(emp.absent_days > 0 || emp.half_days > 0) && (
                        <span className="text-[10px] text-red-400">{emp.absent_days} غياب · {emp.half_days} نصف يوم</span>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <button onClick={() => { setSelectedEmp(emp); setPayAmount(""); setPayDate(new Date().toISOString().split("T")[0]); setShowPayModal(true); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-100" data-testid={`pay-btn-${emp.id}`}>
                      <DollarSign className="w-3.5 h-3.5" /> دفع راتب
                    </button>
                    <button onClick={() => { setSelectedEmp(emp); setAttForm({ date: new Date().toISOString().split("T")[0], status: "absent", reason: "", deduction_amount: 20000 }); setShowAttModal(true); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-100" data-testid={`att-btn-${emp.id}`}>
                      <Clock className="w-3.5 h-3.5" /> تسجيل غياب
                    </button>
                    <button onClick={() => setExpanded(isExpanded ? null : emp.id)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-gray-50 text-gray-600 rounded-lg text-xs hover:bg-gray-100">
                      {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      التفاصيل
                    </button>
                    <button onClick={() => handleDelete(emp.id, emp.name)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-gray-50 text-red-500 rounded-lg text-xs hover:bg-red-50 mr-auto" data-testid={`del-emp-${emp.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50 p-4 space-y-4">
                    {/* Payments */}
                    <div>
                      <h4 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-1">
                        <DollarSign className="w-4 h-4 text-emerald-500" /> دفعات الشهر
                      </h4>
                      {emp.payments?.length > 0 ? (
                        <div className="space-y-1.5">
                          {emp.payments.map((p, i) => (
                            <div key={p.id || `payment-${i}`} className="flex items-center justify-between bg-white p-2.5 rounded-lg border border-gray-100 text-sm">
                              <span className="text-gray-500">{p.date}</span>
                              <span className="font-bold text-emerald-600">{(p.amount || 0).toLocaleString()}</span>
                              <span className="text-gray-400 text-xs">{p.description || ""}</span>
                            </div>
                          ))}
                        </div>
                      ) : <p className="text-sm text-gray-400 text-center py-3">لا توجد دفعات هذا الشهر</p>}
                    </div>

                    {/* Attendance */}
                    <div>
                      <h4 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-red-500" /> سجل الغياب
                      </h4>
                      {emp.attendance?.length > 0 ? (
                        <div className="space-y-1.5">
                          {emp.attendance.map((r, i) => (
                            <div key={r.id || `attendance-${i}`} className="flex items-center justify-between bg-white p-2.5 rounded-lg border border-gray-100 text-sm">
                              <span className="text-gray-500">{r.date}</span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.status === "absent" ? "bg-red-100 text-red-700" : r.status === "half_day" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>
                                {r.status === "absent" ? "غياب" : r.status === "half_day" ? "نصف يوم" : "إجازة"}
                              </span>
                              <span className="text-red-500 text-xs font-medium">-{(r.deduction_amount || 0).toLocaleString()}</span>
                              <span className="text-gray-400 text-xs">{r.reason || ""}</span>
                              <button onClick={() => deleteAttendance(r.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                          ))}
                        </div>
                      ) : <p className="text-sm text-gray-400 text-center py-3">لا يوجد غياب هذا الشهر</p>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pay Modal */}
      {showPayModal && selectedEmp && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowPayModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-5" onClick={e => e.stopPropagation()} data-testid="pay-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">دفع راتب</h3>
              <button onClick={() => setShowPayModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="text-center py-2">
              <p className="text-xl font-bold">{selectedEmp.name}</p>
              <p className="text-sm text-gray-500">المتبقي: <span className="font-bold text-blue-700">{selectedEmp.net_remaining.toLocaleString()}</span></p>
            </div>
            {/* Quick amounts */}
            <div className="grid grid-cols-4 gap-2">
              {[10000, 20000, 25000, 50000].map(amt => (
                <button key={amt} onClick={() => setPayAmount(String(amt))}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${payAmount === String(amt) ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-emerald-300'}`}>
                  <span className="text-sm font-bold">{amt.toLocaleString()}</span>
                </button>
              ))}
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">مبلغ مخصص</label>
              <input type="number" value={payAmount} onChange={e => setPayAmount(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-center text-xl font-bold focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="0" data-testid="pay-amount-input" />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">تاريخ الدفع</label>
              <input type="date" value={payDate} onChange={e => setPayDate(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-center focus:ring-2 focus:ring-emerald-500" data-testid="pay-date-input" />
            </div>
            <button onClick={handlePay} disabled={!payAmount}
              className="w-full py-3 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 disabled:opacity-40 flex items-center justify-center gap-2" data-testid="confirm-pay-btn">
              <CheckCircle className="w-5 h-5" /> تأكيد الدفع
            </button>
          </div>
        </div>
      )}

      {/* Add Employee Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="add-employee-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">إضافة موظف</h3>
              <button onClick={() => setShowAddModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">اسم الموظف *</label>
              <input type="text" value={addForm.name} onChange={e => setAddForm({...addForm, name: e.target.value})}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500" placeholder="الاسم" data-testid="emp-name-input" />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">المنصب</label>
              <input type="text" value={addForm.position} onChange={e => setAddForm({...addForm, position: e.target.value})}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500" placeholder="محاسب، مدير..." data-testid="emp-position-input" />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">الراتب الشهري</label>
              <input type="number" value={addForm.salary} onChange={e => setAddForm({...addForm, salary: e.target.value})}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500" placeholder="0" data-testid="emp-salary-input" />
            </div>
            <button onClick={handleAdd}
              className="w-full py-3 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 flex items-center justify-center gap-2" data-testid="confirm-add-btn">
              <Plus className="w-5 h-5" /> إضافة
            </button>
          </div>
        </div>
      )}

      {/* Attendance Modal */}
      {showAttModal && selectedEmp && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAttModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="attendance-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">تسجيل غياب - {selectedEmp.name}</h3>
              <button onClick={() => setShowAttModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">التاريخ</label>
              <input type="date" value={attForm.date} onChange={e => setAttForm({...attForm, date: e.target.value})}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl" data-testid="att-date-input" />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2">الحالة</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { val: "absent", label: "غياب", color: "red", ded: 20000 },
                  { val: "half_day", label: "نصف يوم", color: "amber", ded: 10000 },
                  { val: "leave", label: "إجازة", color: "blue", ded: 0 }
                ].map(s => (
                  <button key={s.val} onClick={() => setAttForm({...attForm, status: s.val, deduction_amount: s.ded})}
                    className={`p-3 rounded-xl border-2 text-center text-sm font-medium transition-all ${attForm.status === s.val ? `border-${s.color}-500 bg-${s.color}-50 text-${s.color}-700` : 'border-gray-200 hover:border-gray-300'}`}
                    data-testid={`att-status-${s.val}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">مبلغ الخصم</label>
              <input type="number" value={attForm.deduction_amount} onChange={e => setAttForm({...attForm, deduction_amount: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-center font-bold" data-testid="att-deduction-input" />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">السبب (اختياري)</label>
              <input type="text" value={attForm.reason} onChange={e => setAttForm({...attForm, reason: e.target.value})}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl" placeholder="سبب الغياب..." data-testid="att-reason-input" />
            </div>
            <button onClick={handleAttendance}
              className="w-full py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 flex items-center justify-center gap-2" data-testid="confirm-att-btn">
              <CheckCircle className="w-5 h-5" /> تسجيل
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollPage;
